import { Link, useNavigate, useSearch } from '@tanstack/react-router'
import { ArrowDown, ArrowUp, ChevronRight, FilePlus2, FileText, Receipt, Search, Sparkles, Trash2 } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { InvoicePanel } from '@/components/invoices/InvoicePanel'
import { InvoiceStatusPill } from '@/components/invoices/InvoiceStatusPill'
import { OpenDurationBadge } from '@/components/invoices/OpenDurationBadge'
import { StatusLegend } from '@/components/invoices/StatusLegend'
import { MonthlyWizard } from '@/components/invoices/MonthlyWizard'
import { AttentionStrip } from '@/components/ui/AttentionStrip'
import { Modal } from '@/components/ui/Modal'
import { EmptyState } from '@/components/ui/EmptyState'
import { ErrorState } from '@/components/ui/ErrorState'
import { Money } from '@/components/ui/Money'
import { PageHeader } from '@/components/ui/PageHeader'
import { SegmentedControl, type Segment } from '@/components/ui/SegmentedControl'
import { Select } from '@/components/ui/Select'
import { Skeleton } from '@/components/ui/Skeleton'
import { useIsAdmin } from '@/lib/auth'
import { toNumber } from '@/lib/decimal'
import {
  DEFAULT_FILTER,
  bucketDefinitions,
  emptyCopy,
  filterParams,
  matchesFilter,
  subFilters,
  type Bucket,
  type Filter,
} from '@/lib/invoice-buckets'
import { bulkGroups, isSelectable, type BulkGroup } from '@/lib/invoice-bulk'
import { useBulkTransition, useInvoiceList } from '@/lib/invoice-queries'
import { isInvoiceStatus } from '@/lib/invoice-status-copy'
import type { BulkTransitionAction, InvoiceListItem, InvoiceStatus } from '@/lib/invoice-types'
import { cn } from '@/lib/utils'
import { todayIsoDate } from '@/lib/week'

type SortKey = 'number' | 'client' | 'total' | 'status' | 'date'
type SortDir = 'asc' | 'desc'

const STATUS_ORDER: Record<InvoiceStatus, number> = {
  DRAFT: 0,
  PARKED: 1,
  PENDING_APPROVAL: 2,
  APPROVED: 3,
  SENT: 4,
  ESCALATED: 5,
  PAID: 6,
  WITHDRAWN: 7,
  WRITTEN_OFF: 8,
  VOIDED: 9,
  REPLACED: 10,
}

const COMPARATORS: Record<SortKey, (a: InvoiceListItem, b: InvoiceListItem) => number> = {
  number: (a, b) => (a.fullNumber ?? '￿').localeCompare(b.fullNumber ?? '￿'),
  client: (a, b) => a.clientName.localeCompare(b.clientName, 'nl'),
  total: (a, b) => toNumber(a.total) - toNumber(b.total),
  status: (a, b) => STATUS_ORDER[a.status] - STATUS_ORDER[b.status],
  date: (a, b) => (a.issueDate ?? '').localeCompare(b.issueDate ?? ''),
}

function isRecent(invoice: InvoiceListItem): boolean {
  if (invoice.status !== 'DRAFT' || !invoice.createdAt) return false
  return Date.now() - new Date(invoice.createdAt).getTime() < 24 * 60 * 60 * 1000
}

export function InvoicesPage() {
  const isAdmin = useIsAdmin()
  const routeSearch = useSearch({ strict: false }) as { wizard?: boolean; bucket?: string; status?: string }
  const navigate = useNavigate()
  const bucketFromUrl =
    routeSearch.bucket && bucketDefinitions().some((d) => d.value === routeSearch.bucket)
      ? (routeSearch.bucket as Bucket)
      : null
  const statusFromUrl = isInvoiceStatus(routeSearch.status) ? routeSearch.status : null
  const [filter, setFilter] = useState<Filter | null>(bucketFromUrl ? { bucket: bucketFromUrl, status: statusFromUrl } : null)
  const [year, setYear] = useState<number | undefined>(undefined)
  const [search, setSearch] = useState('')
  const [wizardOpen, setWizardOpen] = useState(false)
  const [sort, setSort] = useState<{ key: SortKey; dir: SortDir } | null>(null)
  const [panelId, setPanelId] = useState<string | null>(null)
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set())
  const [issueDate, setIssueDate] = useState(() => todayIsoDate())
  const [confirming, setConfirming] = useState<BulkGroup | null>(null)
  const defaultApplied = useRef(bucketFromUrl !== null)

  const bulk = useBulkTransition()

  useEffect(() => {
    if (routeSearch.wizard) {
      setWizardOpen(true)
      navigate({ to: '/facturen', search: (prev) => ({ ...prev, wizard: undefined }), replace: true })
    }
  }, [routeSearch.wizard, navigate])

  const allQuery = useInvoiceList({ status: 'ALL' })

  useEffect(() => {
    if (defaultApplied.current || !allQuery.data) return
    defaultApplied.current = true
    if (filter === null) {
      const hasWip = allQuery.data.some((invoice) => matchesFilter(invoice, DEFAULT_FILTER))
      setFilter(hasWip ? DEFAULT_FILTER : { bucket: 'ALL', status: null })
    }
  }, [allQuery.data, filter])

  const activeFilter: Filter = filter ?? { bucket: 'ALL', status: null }
  const bucket = activeFilter.bucket

  const listQuery = useInvoiceList({
    ...filterParams(activeFilter),
    year,
    search: search.trim() || undefined,
  })

  const years = useMemo(() => {
    const set = new Set<number>((allQuery.data ?? []).map((invoice) => invoice.year))
    set.add(new Date().getFullYear())
    if (year !== undefined) set.add(year)
    return [...set].sort((a, b) => b - a)
  }, [allQuery.data, year])

  /**
   * De tellers draaien over de al opgehaalde lijst, met hetzelfde jaar- en zoekfilter
   * erop. Zonder die twee zou een segment "8" tonen terwijl de lijst eronder leeg is.
   */
  const scoped = useMemo(() => {
    const q = search.trim().toLowerCase()
    return (allQuery.data ?? []).filter((invoice) => {
      if (year !== undefined && invoice.year !== year) return false
      if (!q) return true
      return (
        (invoice.fullNumber ?? '').toLowerCase().includes(q)
        || invoice.clientName.toLowerCase().includes(q)
        || invoice.subject.toLowerCase().includes(q)
      )
    })
  }, [allQuery.data, year, search])

  const segments: Segment<Bucket>[] = useMemo(
    () =>
      bucketDefinitions()
        .map((definition) => ({
          value: definition.value,
          label: definition.label,
          count: allQuery.data ? scoped.filter((i) => matchesFilter(i, { bucket: definition.value, status: null })).length : undefined,
        }))
        .filter((segment) => segment.value !== 'ATTENTION' || segment.count !== 0 || bucket === 'ATTENTION'),
    [allQuery.data, scoped, bucket],
  )

  const chips = useMemo(() => subFilters(bucket, scoped), [bucket, scoped])

  const attention = useMemo(() => {
    const rows = scoped.filter((i) => i.needsAttention)
    return { count: rows.length, amount: rows.reduce((sum, i) => sum + toNumber(i.total), 0) }
  }, [scoped])

  const invoices = useMemo(() => {
    const list = listQuery.data ?? []
    // Oudste bovenaan in "Te lang open": de volgorde draagt de urgentie, zodat er
    // geen tweede drempel of tweede kleur nodig is.
    if (!sort) return bucket === 'ATTENTION' ? [...list].sort((a, b) => b.daysOpen - a.daysOpen) : list
    const compare = COMPARATORS[sort.key]
    const sorted = [...list].sort(compare)
    return sort.dir === 'desc' ? sorted.reverse() : sorted
  }, [listQuery.data, sort, bucket])

  const filteredTotal = useMemo(() => invoices.reduce((sum, invoice) => sum + toNumber(invoice.total), 0), [invoices])
  const filteredSubtotal = useMemo(() => invoices.reduce((sum, invoice) => sum + toNumber(invoice.subtotal), 0), [invoices])

  /** Alles wat een bulk-actie kent, ongeacht status: dat is wat "alles selecteren" pakt. */
  const selectable = useMemo(() => invoices.filter(isSelectable), [invoices])
  const showCheckboxes = isAdmin && selectable.length > 0
  const groups = useMemo(() => (isAdmin ? bulkGroups(invoices, selected) : []), [isAdmin, invoices, selected])
  const selectedCount = useMemo(() => groups.reduce((sum, group) => sum + group.ids.length, 0), [groups])
  const needsIssueDate = groups.some((group) => group.actions.some((action) => action.needsIssueDate))

  function toggleSort(key: SortKey) {
    setSort((current) => {
      if (current?.key === key) return { key, dir: current.dir === 'asc' ? 'desc' : 'asc' }
      return { key, dir: key === 'total' || key === 'date' ? 'desc' : 'asc' }
    })
  }

  function changeFilter(next: Filter) {
    setFilter(next)
    clearSelection()
    navigate({
      to: '/facturen',
      search: (prev) => ({ ...prev, bucket: next.bucket, status: next.status ?? undefined }),
      replace: true,
    })
  }

  function clearSelection() {
    setSelected(new Set())
  }

  function toggleSelected(invoice: InvoiceListItem) {
    const next = new Set(selected)
    if (!next.delete(invoice.id)) next.add(invoice.id)
    setSelected(next)
  }

  function toggleAll() {
    if (selectable.length === 0) return
    if (selectedCount >= selectable.length) {
      clearSelection()
      return
    }
    setSelected(new Set(selectable.map((invoice) => invoice.id)))
  }

  function runBulk(group: BulkGroup, action: BulkTransitionAction, withIssueDate?: boolean) {
    if (group.ids.length === 0) return
    bulk.mutate(
      { ids: group.ids, action, issueDate: withIssueDate ? issueDate : undefined },
      { onSuccess: () => clearSelection() },
    )
  }
  // border-l-2 hoort op de grid en niet op de rij zelf: divide-hairline overschrijft
  // de randkleur van elk direct kind na de eerste, dus daar wint hij het niet.
  const grid = showCheckboxes
    ? 'grid grid-cols-[2rem_6.5rem_1fr_9rem_8rem_6.5rem_4rem] items-center gap-4 border-l-2 px-5'
    : 'grid grid-cols-[6.5rem_1fr_9rem_8rem_6.5rem_4rem] items-center gap-4 border-l-2 px-5'

  return (
    <div className="space-y-6">
      <PageHeader
        title="Facturen"
        description="Concepten, verzonden en betaalde facturen. Start het maandproces via de wizard."
        actions={
          isAdmin ? (
            <>
              <button type="button" className="btn-secondary" onClick={() => setWizardOpen(true)}>
                <Sparkles className="h-4 w-4" />
                Maandfacturen genereren
              </button>
              <Link to="/facturen/nieuw" className="btn-primary">
                <FilePlus2 className="h-4 w-4" />
                Nieuwe factuur
              </Link>
            </>
          ) : null
        }
      />

      <div className="space-y-2.5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex min-w-0 items-center gap-1">
            <SegmentedControl
              label="Filter facturen"
              segments={segments}
              value={bucket}
              onChange={(next) => changeFilter({ bucket: next, status: null })}
            />
            <StatusLegend />
          </div>
          <div className="flex items-center gap-2">
            <div className="flex h-9 items-center gap-2 rounded-lg border border-hairline bg-surface px-2.5 text-sm text-ink-faint">
              <Search className="h-4 w-4" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Zoek op nummer of klant"
                className="w-44 bg-transparent text-ink outline-hidden placeholder:text-ink-faint"
              />
            </div>
            <Select
              value={year === undefined ? 'all' : String(year)}
              onValueChange={(v) => setYear(v === 'all' ? undefined : Number(v))}
              options={[{ value: 'all', label: 'Alle jaren' }, ...years.map((y) => ({ value: String(y), label: String(y) }))]}
              className="w-28"
            />
          </div>
        </div>

        {chips.length > 0 ? (
          <div className="flex flex-wrap items-center gap-x-1 gap-y-1.5" role="group" aria-label="Filter op status">
            <StatusChip
              label="Alles"
              count={chips.reduce((sum, chip) => sum + chip.count, 0)}
              active={activeFilter.status === null}
              onClick={() => changeFilter({ bucket, status: null })}
            />
            {chips.map((chip) => (
              <StatusChip
                key={chip.status}
                label={chip.label}
                count={chip.count}
                active={activeFilter.status === chip.status}
                onClick={() => changeFilter({ bucket, status: chip.status })}
              />
            ))}
          </div>
        ) : null}
      </div>

      {bucket !== 'ATTENTION' && attention.count > 0 ? (
        <AttentionStrip
          amount={attention.amount}
          headline="staat te lang open"
          description={`${attention.count === 1 ? '1 factuur wacht' : `${attention.count} facturen wachten`} al te lang op betaling.`}
          action={
            <button
              type="button"
              className="btn-ghost h-8 whitespace-nowrap px-3 text-sm"
              onClick={() => changeFilter({ bucket: 'ATTENTION', status: null })}
            >
              Bekijk deze facturen
            </button>
          }
        />
      ) : null}

      {groups.length > 0 ? (
        <div className="space-y-2 rounded-card border border-hairline bg-surface px-4 py-2.5 shadow-card">
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-sm font-medium text-ink">{selectedCount} geselecteerd</span>
            {needsIssueDate ? (
              <label className="flex items-center gap-2 text-sm text-ink-soft">
                Factuurdatum
                <input
                  type="date"
                  value={issueDate}
                  onChange={(e) => setIssueDate(e.target.value)}
                  className="h-8 rounded-lg border border-hairline bg-surface px-2 text-sm text-ink outline-hidden"
                />
              </label>
            ) : null}
            <button type="button" className="btn-ghost ml-auto h-8 px-2.5 text-sm" onClick={clearSelection}>
              Selectie wissen
            </button>
          </div>
          {/* Een selectie mag door de statussen heen lopen, een transitie niet: elke rij
              hieronder is één status en zijn knoppen raken alleen die facturen. */}
          {groups.map((group) => (
            <div key={group.status} className="flex flex-wrap items-center gap-2 border-t border-hairline pt-2 first:border-t-0 first:pt-0">
              <InvoiceStatusPill status={group.status} />
              <span className="num text-sm text-ink-soft">{group.ids.length}</span>
              <div className="ml-auto flex flex-wrap items-center justify-end gap-2">
                {group.actions.map(({ action, label, icon: Icon, needsIssueDate: withIssueDate, danger }) => (
                  <button
                    key={action}
                    type="button"
                    className={cn('btn-primary', danger && 'bg-status-overdue hover:bg-status-overdue/90')}
                    disabled={bulk.isPending}
                    onClick={() => (danger ? setConfirming(group) : runBulk(group, action, withIssueDate))}
                  >
                    <Icon className="h-4 w-4" />
                    {label}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : null}

      <div className="overflow-hidden rounded-card bg-surface shadow-card">
        <div className={cn(grid, 'hidden border-b border-hairline border-l-transparent py-1.5 sm:grid')}>
          {showCheckboxes ? (
            <input
              type="checkbox"
              aria-label="Selecteer alles"
              title="Selecteer alle facturen waar een bulk-actie op kan"
              checked={selectedCount >= selectable.length}
              ref={(el) => {
                if (el) el.indeterminate = selectedCount > 0 && selectedCount < selectable.length
              }}
              onChange={toggleAll}
              className="h-4 w-4 accent-accent-600"
            />
          ) : null}
          <SortHeader label="Nummer" sortKey="number" sort={sort} onSort={toggleSort} />
          <SortHeader label="Klant · Betreft" sortKey="client" sort={sort} onSort={toggleSort} />
          <SortHeader label="Bedrag" sortKey="total" sort={sort} onSort={toggleSort} align="right" />
          <SortHeader label="Status" sortKey="status" sort={sort} onSort={toggleSort} />
          <SortHeader label="Datum" sortKey="date" sort={sort} onSort={toggleSort} align="right" />
          <span aria-hidden />
        </div>

        {listQuery.isError ? (
          <ErrorState compact error={listQuery.error} onRetry={() => listQuery.refetch()} />
        ) : listQuery.isLoading || filter === null ? (
          <div className="divide-y divide-hairline">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className={cn(grid, 'border-l-transparent py-3.5')}>
                {showCheckboxes ? <span /> : null}
                <Skeleton className="h-4 w-16" />
                <Skeleton className="h-4 w-48" />
                <Skeleton className="ml-auto h-4 w-20" />
                <Skeleton className="h-5 w-24 rounded-pill" />
                <Skeleton className="ml-auto h-4 w-16" />
                <span />
              </div>
            ))}
          </div>
        ) : invoices.length === 0 ? (
          <EmptyState
            icon={Receipt}
            className="border-0 bg-transparent"
            title={emptyCopy(activeFilter).title}
            description={search.trim() ? `Geen resultaat voor "${search.trim()}".` : emptyCopy(activeFilter).description}
          />
        ) : (
          <>
          <div className="divide-y divide-hairline">
            {invoices.map((invoice) => {
              const recent = isRecent(invoice)
              return (
              <div key={invoice.id} className={cn('group relative transition-colors hover:bg-fill-1', recent && 'bg-accent-600/4')}>
                <Link
                  to="/facturen/$id"
                  params={{ id: invoice.id }}
                  aria-label={`Factuur ${invoice.fullNumber ?? 'concept'} openen`}
                  className="absolute inset-0 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-accent-500/40"
                  onClick={(e) => {
                    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return
                    e.preventDefault()
                    setPanelId(invoice.id)
                  }}
                />
                <span
                  className={cn(
                    grid,
                    'pointer-events-none hidden py-3.5 sm:grid',
                    // Alle rijen dragen de rand zodat de uitlijning gelijk blijft.
                    invoice.needsAttention ? 'border-l-status-overdue' : 'border-l-transparent',
                  )}
                >
                  {showCheckboxes ? (
                    <input
                      type="checkbox"
                      aria-label={`Selecteer ${invoice.fullNumber ?? invoice.subject}`}
                      checked={selected.has(invoice.id)}
                      onChange={() => toggleSelected(invoice)}
                      onClick={(e) => e.stopPropagation()}
                      className="pointer-events-auto relative z-10 h-4 w-4 accent-accent-600"
                      disabled={!isSelectable(invoice)}
                    />
                  ) : null}
                  <span className="num text-sm font-medium text-ink">{invoice.fullNumber ?? 'concept'}</span>
                  <span className="flex min-w-0 items-center gap-2.5">
                    <span
                      className="h-2.5 w-2.5 shrink-0 rounded-full"
                      style={{ backgroundColor: invoice.templateColor }}
                      title={invoice.templateCode}
                    />
                    <span className="min-w-0">
                      <span className="flex items-center gap-2 truncate font-medium text-ink">
                        {invoice.clientName}
                        {recent ? <span className="rounded-pill bg-accent-600/10 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-accent-700">Nieuw</span> : null}
                      </span>
                      <span className="block truncate text-sm text-ink-faint">
                        {invoice.subject}
                        {invoice.periodLabel ? ` · ${invoice.periodLabel}` : ''}
                        {invoice.status === 'PARKED' && invoice.parkReason ? ` · ${invoice.parkReason}` : ''}
                        {invoice.status === 'PARKED' && invoice.parkReleaseDate ? ` · vrijgave ${invoice.parkReleaseDate}` : ''}
                      </span>
                    </span>
                  </span>
                  <Money amount={toNumber(invoice.subtotal)} secondaryAmount={toNumber(invoice.total)} className="text-sm font-medium text-ink" />
                  <span className="min-w-0">
                    <span className="flex items-center gap-2">
                      <InvoiceStatusPill status={invoice.status} detail={invoice} />
                      {invoice.stale ? <span className="h-1.5 w-1.5 rounded-full bg-status-approval" title="Stale" /> : null}
                    </span>
                    <OpenDurationBadge
                      daysOpen={invoice.daysOpen}
                      needsAttention={invoice.needsAttention}
                      className="pointer-events-auto mt-1"
                    />
                  </span>
                  <span className="num text-right text-sm text-ink-faint">{invoice.issueDate ?? '-'}</span>
                  <span className="pointer-events-auto flex items-center justify-end gap-1">
                    <a
                      href={`/api/invoices/${invoice.id}/pdf`}
                      target="_blank"
                      rel="noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      aria-label="Open PDF"
                      className="flex h-7 w-7 items-center justify-center rounded-md text-ink-faint opacity-0 transition-all duration-150 hover:bg-fill-2 hover:text-ink group-hover:opacity-100"
                    >
                      <FileText className="h-4 w-4" />
                    </a>
                    <button
                      type="button"
                      onClick={() => setPanelId(invoice.id)}
                      aria-label="Open paneel"
                      className="flex h-7 w-7 items-center justify-center rounded-md text-ink-faint opacity-0 transition-all duration-150 hover:bg-fill-2 hover:text-ink group-hover:opacity-100"
                    >
                      <ChevronRight className="h-4 w-4" />
                    </button>
                  </span>
                </span>
                <span
                  className={cn(
                    'pointer-events-none flex items-center justify-between gap-3 border-l-2 px-5 py-3 sm:hidden',
                    invoice.needsAttention ? 'border-l-status-overdue' : 'border-l-transparent',
                  )}
                >
                  <span className="flex min-w-0 items-center gap-2.5">
                    <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: invoice.templateColor }} />
                    <span className="min-w-0">
                      <span className="block truncate font-medium text-ink">{invoice.clientName}</span>
                      <span className="block truncate text-sm text-ink-faint">
                        {invoice.fullNumber ?? 'concept'}
                        {invoice.issueDate ? ` · ${invoice.issueDate}` : ''} · {invoice.subject}
                      </span>
                    </span>
                  </span>
                  <span className="shrink-0 text-right">
                    <Money amount={toNumber(invoice.subtotal)} secondaryAmount={toNumber(invoice.total)} className="text-sm font-medium text-ink" />
                    <span className="mt-1 flex items-center justify-end gap-1.5"><OpenDurationBadge daysOpen={invoice.daysOpen} needsAttention={invoice.needsAttention} /><InvoiceStatusPill status={invoice.status} detail={invoice} /></span>
                  </span>
                </span>
              </div>
              )
            })}
          </div>
          <div className={cn(grid, 'hidden border-t border-hairline-strong border-l-transparent py-3 sm:grid')}>
            {showCheckboxes ? <span /> : null}
            <span className="col-span-2 text-xs text-ink-faint">{invoices.length} facturen</span>
            <Money amount={filteredSubtotal} secondaryAmount={filteredTotal} className="text-sm font-semibold text-ink" />
          </div>
          <div className="flex items-center justify-between border-t border-hairline-strong px-5 py-3 sm:hidden">
            <span className="text-xs text-ink-faint">{invoices.length} facturen</span>
            <Money amount={filteredSubtotal} secondaryAmount={filteredTotal} className="text-sm font-semibold text-ink" />
          </div>
          </>
        )}
      </div>

      {confirming ? (
        <Modal
          open
          onOpenChange={(o) => !o && setConfirming(null)}
          title={`${confirming.ids.length} factu${confirming.ids.length === 1 ? 'ur' : 'ren'} verwijderen`}
          description="Ze verdwijnen definitief. De uren en reiskosten die erin zaten komen weer vrij en kunnen opnieuw gefactureerd worden."
          footer={
            <>
              <button type="button" className="btn-ghost" onClick={() => setConfirming(null)}>
                Annuleren
              </button>
              <button
                type="button"
                className="btn-primary bg-status-overdue hover:bg-status-overdue/90"
                disabled={bulk.isPending}
                onClick={() => { runBulk(confirming, 'delete'); setConfirming(null) }}
              >
                <Trash2 className="h-4 w-4" />
                Verwijder
              </button>
            </>
          }
        >
          <p className="text-sm text-ink-soft">Weet je het zeker?</p>
        </Modal>
      ) : null}

      <MonthlyWizard open={wizardOpen} onOpenChange={setWizardOpen} />
      <InvoicePanel
        invoiceId={panelId}
        open={panelId !== null}
        onOpenChange={(open) => {
          if (!open) setPanelId(null)
        }}
      />
    </div>
  )
}

/**
 * Tweede filterlaag onder de segmenten. Bewust vlak en zonder rand: de segmenten
 * dragen de omlijnde vorm, dus een tweede omlijnde groep eronder zou als een
 * concurrerende balk lezen in plaats van als een verfijning.
 */
function StatusChip({
  label,
  count,
  active,
  onClick,
}: {
  label: string
  count: number
  active: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1 text-sm transition-colors duration-150',
        active ? 'bg-fill-2 font-medium text-ink' : 'text-ink-soft hover:bg-fill-1 hover:text-ink',
      )}
    >
      {label}
      <span className={cn('num text-xs', active ? 'text-ink-soft' : 'text-ink-faint')}>{count}</span>
    </button>
  )
}

function SortHeader({
  label,
  sortKey,
  sort,
  onSort,
  align,
}: {
  label: string
  sortKey: SortKey
  sort: { key: SortKey; dir: SortDir } | null
  onSort: (key: SortKey) => void
  align?: 'right'
}) {
  const active = sort?.key === sortKey
  const Arrow = active && sort?.dir === 'desc' ? ArrowDown : ArrowUp
  return (
    <button
      type="button"
      onClick={() => onSort(sortKey)}
      role="columnheader"
      aria-sort={active ? (sort?.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
      className={cn(
        'group -mx-1.5 flex items-center gap-1 rounded-md px-1.5 py-1.5 text-[11px] font-semibold uppercase tracking-[0.08em] transition-colors hover:text-ink',
        active ? 'text-ink' : 'text-ink-faint',
        align === 'right' && 'justify-end text-right',
      )}
    >
      {label}
      <Arrow className={cn('h-3 w-3 transition-opacity', active ? 'opacity-100' : 'opacity-0 group-hover:opacity-40')} />
    </button>
  )
}
